import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Stepper, Step, StepLabel,
  Typography, Tooltip, IconButton,
} from '@mui/material';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow, InvTextField } from '../../components/InvField';
import {
  getPurchaseInvoice, matchPurchaseInvoice, postPurchaseInvoiceToFinance, reversePurchaseInvoice,
} from '../../api/purchaseInvoice';
import { getPurchaseOrder, getPurchaseOrderItems } from '../../api/procurement';
import { getGrn, getGrnItems } from '../../api/grn';
import { getVendors } from '../../api/vendor';
import useItemNames from '../../hooks/useItemNames';
import { formatDate, formatDateTime, formatCurrency, formatQty } from '../../utils';
import { extractInvoiceScope, computeInvoiceFinancials, computeLineVariance, findPoItemForGrnLine, formatCreateInvoiceError, invoiceLineTotal, normalizeInvoiceLines, parseInvoiceAmount, validateMatchTolerance, validateOverrideReason, OVERRIDE_REASON_MIN_LENGTH } from '../../utils/purchaseInvoiceEntity';
import { DEFAULT_MATCH_TOLERANCE_PERCENT } from '../../constants';
import { IPD_COLORS, cardSx } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

const LIFECYCLE_STEPS = ['DRAFT', 'MATCHED', 'POSTED'];
const MATCH_RUN_STATUSES = new Set(['MATCHED', 'VARIANCE', 'POSTED', 'REVERSED']);

function DeltaCell({ value, hasVariance, format }) {
  if (value == null || !hasVariance) {
    return <Box component="span" sx={{ color: IPD_COLORS.textSecondary }}>—</Box>;
  }
  const sign = value > 0 ? '+' : '';
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: IPD_COLORS.warning.text }}>
      <WarningAmberIcon sx={{ fontSize: 14 }} aria-hidden="true" />
      <Typography component="span" sx={{ fontSize: 13, fontFamily: 'monospace', color: 'inherit' }}>
        {sign}{format(value)}
      </Typography>
    </Box>
  );
}

function VarianceBreakdownSection({ lines, poItems, grnItems, loading, resolveItem }) {
  if (loading) {
    return (
      <SectionCard title="3-Way Match Comparison" sx={{ mt: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: IPD_COLORS.primary }} />
        </Box>
      </SectionCard>
    );
  }

  if (!poItems.length && !grnItems.length) {
    return (
      <SectionCard title="3-Way Match Comparison" sx={{ mt: 2.5 }}>
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, py: 1 }}>
          Match details unavailable — PO or GRN line data could not be loaded.
        </Typography>
      </SectionCard>
    );
  }

  const rows = lines.map((line, i) => {
    const poItem = findPoItemForGrnLine(line, poItems);
    const grnItem = grnItems.find(g =>
      line.grn_item_id && (g.grn_item_id || g.id) === line.grn_item_id,
    ) ?? grnItems.find(g =>
      line.item_id && String(g.item_id) === String(line.item_id),
    ) ?? null;
    return {
      key: line.purchase_invoice_line_id || line.id || i,
      label: resolveItem(line),
      ...computeLineVariance(line, poItem, grnItem),
    };
  });

  return (
    <SectionCard title="3-Way Match Comparison" sx={{ mt: 2.5 }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          aria-label="3-way match comparison"
          sx={{ minWidth: 760, '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>GRN Accepted Qty</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Invoice Qty</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty Δ</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>PO Rate</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Invoice Rate</TableCell>
              <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rate Δ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.key}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.grnQty != null ? formatQty(row.grnQty) : '—'}</TableCell>
                <TableCell align="right">{row.invoiceQty != null ? formatQty(row.invoiceQty) : '—'}</TableCell>
                <TableCell align="right">
                  <DeltaCell value={row.qtyDelta} hasVariance={row.hasQtyVariance} format={formatQty} />
                </TableCell>
                <TableCell align="right">{row.poRate != null ? formatCurrency(row.poRate) : '—'}</TableCell>
                <TableCell align="right">{row.invoiceRate != null ? formatCurrency(row.invoiceRate) : '—'}</TableCell>
                <TableCell align="right">
                  <DeltaCell value={row.rateDelta} hasVariance={row.hasRateVariance} format={formatCurrency} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SectionCard>
  );
}

function PaymentStatusBadge({ status }) {
  if (!status) {
    return (
      <Typography component="span" sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, fontStyle: 'italic' }}>
        Awaiting finance update
      </Typography>
    );
  }
  if (String(status).toUpperCase() === 'FAILED') {
    return (
      <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.5 }}>
        <StatusChip status={status} />
        <Typography component="span" sx={{ fontSize: 12, color: IPD_COLORS.warning.text }}>
          Contact finance to retry payment
        </Typography>
      </Box>
    );
  }
  return <StatusChip status={status} />;
}

export default function PurchaseInvoiceDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { resolveItem } = useItemNames();
  const base = url.replace(/\/purchase-invoice\/.*/, '');

  const [invoice, setInvoice] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [poNumber, setPoNumber] = useState(null);
  const [grnNumber, setGrnNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [matchOpen, setMatchOpen] = useState(false);
  const [tolerance, setTolerance] = useState(String(DEFAULT_MATCH_TOLERANCE_PERCENT));
  const [matching, setMatching] = useState(false);

  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);

  const [poItems, setPoItems] = useState([]);
  const [grnItems, setGrnItems] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [financeRefCopied, setFinanceRefCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      // API: GET /inventory/purchase-invoices/{id}/
      const inv = await getPurchaseInvoice(id);
      setInvoice(inv);

      if (inv.purchase_order_id || inv.grn_id) {
        const scope = extractInvoiceScope(inv);
        setComparisonLoading(true);
        Promise.all([
          inv.purchase_order_id
            ? getPurchaseOrderItems(inv.purchase_order_id, scope).catch(() => [])
            : Promise.resolve([]),
          inv.grn_id
            ? getGrnItems(inv.grn_id).catch(() => [])
            : Promise.resolve([]),
        ]).then(([poData, grnData]) => {
          setPoItems(normalizeList(poData));
          setGrnItems(normalizeList(grnData));
        }).finally(() => setComparisonLoading(false));
      }

      const vendorsData = await getVendors().catch(() => []);
      setVendors(normalizeList(vendorsData));

      if (inv.purchase_order_id) {
        getPurchaseOrder(inv.purchase_order_id).then(p => setPoNumber(p.po_number)).catch(() => {});
      }
      if (inv.grn_id) {
        getGrn(inv.grn_id).then(g => setGrnNumber(g.grn_number)).catch(() => {});
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  function resolveVendorName(vendorId) {
    if (!vendorId) return '—';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  function handleCopyFinanceRef() {
    const ref = invoice?.finance_ref;
    if (!ref) return;
    navigator.clipboard.writeText(ref).then(() => {
      setFinanceRefCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => setFinanceRefCopied(false), 1500);
    }).catch(() => {});
  }

  async function handleRunMatch() {
    const tolErr = validateMatchTolerance(tolerance);
    if (tolErr) { toast.error(tolErr); return; }

    setMatching(true);
    try {
      const scope = extractInvoiceScope(invoice);
      const result = await matchPurchaseInvoice(id, { tolerance_percent: Number(tolerance) }, scope);
      const resultStatus = result?.status || result?.match_status;
      toast.success(
        resultStatus === 'VARIANCE'
          ? '3-way match completed — variance found against PO/GRN'
          : '3-way match completed — invoice matches PO and GRN',
      );
      setMatchOpen(false);
      load();
    } catch (e) {
      toast.error(formatCreateInvoiceError(e, 'Match failed'));
    } finally {
      setMatching(false);
    }
  }

  async function executeAction() {
    const action = dialog.action;
    if (action === 'reverse' && !reason?.trim()) {
      toast.error('Reversal reason is required');
      return;
    }
    if (action === 'post' && dialog?.requireReason) {
      const reasonError = validateOverrideReason(reason);
      if (reasonError) {
        toast.error(reasonError);
        return;
      }
    }

    setActioning(true);
    try {
      const scope = extractInvoiceScope(invoice);
      if (action === 'post') {
        await postPurchaseInvoiceToFinance(id, { override_reason: reason?.trim() || undefined }, scope);
        toast.success('Invoice posted to finance for payment');
      } else if (action === 'reverse') {
        await reversePurchaseInvoice(id, { reason }, scope);
        toast.success('Invoice reversed');
      }
      setDialog(null); setReason('');
      load();
    } catch (e) {
      toast.error(formatCreateInvoiceError(e, 'Action failed'));
    } finally {
      setActioning(false);
    }
  }

  const lines = normalizeInvoiceLines(invoice);

  const varianceItems = useMemo(() => {
    if (!poItems.length && !grnItems.length) return null;
    return lines.flatMap((line) => {
      const poItem = findPoItemForGrnLine(line, poItems);
      const grnItem = grnItems.find(g =>
        line.grn_item_id && (g.grn_item_id || g.id) === line.grn_item_id,
      ) ?? grnItems.find(g =>
        line.item_id && String(g.item_id) === String(line.item_id),
      ) ?? null;
      const v = computeLineVariance(line, poItem, grnItem);
      return (v.hasQtyVariance || v.hasRateVariance) ? [{ label: resolveItem(line), ...v }] : [];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, poItems, grnItems]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!invoice) return null;

  const status = invoice.status;
  const financials = computeInvoiceFinancials(lines.map(l => ({
    invoiced_quantity: l.invoiced_quantity,
    unit_rate: l.unit_rate,
    discount_percent: l.discount_percent,
    tax_percent: l.tax_percent,
  })));
  const grandTotal = parseInvoiceAmount(invoice.total_amount) ?? financials.grandTotal;

  const activeStep = status === 'REVERSED'
    ? LIFECYCLE_STEPS.length
    : Math.max(0, LIFECYCLE_STEPS.indexOf(status === 'VARIANCE' ? 'MATCHED' : status));

  const actionButtons = [];
  if (status === 'DRAFT') {
    actionButtons.push(
      <Tooltip key="match" title="Step 1 of 2 — run this before posting to finance" arrow>
        <span>
          <InvButton size="small" tone="mint"
            onClick={() => { setTolerance(String(DEFAULT_MATCH_TOLERANCE_PERCENT)); setMatchOpen(true); }}>
            Run 3-Way Match
          </InvButton>
        </span>
      </Tooltip>,
    );
  }
  if (status === 'MATCHED') {
    actionButtons.push(
      <InvButton key="post" size="small"
        onClick={() => setDialog({
          action: 'post', title: 'Post to Finance',
          desc: 'This sends the invoice to the accounts/finance payment queue and cannot be casually undone. Continue?',
        })}>
        Post to Finance
      </InvButton>,
    );
  }
  if (status === 'VARIANCE') {
    actionButtons.push(
      <InvButton key="post-override" size="small" tone="amber"
        onClick={() => setDialog({
          action: 'post', title: 'Post to Finance — Variance Override',
          desc: 'This invoice has a quantity or rate variance against the PO/GRN. Posting it sends the '
            + 'invoice to the finance payment queue despite the mismatch. Your justification is recorded '
            + `in the audit trail — at least ${OVERRIDE_REASON_MIN_LENGTH} characters.`,
          destructive: true, requireReason: true, reasonLabel: 'Override justification',
        })}>
        Post to Finance (Override)
      </InvButton>,
    );
  }
  if (status === 'POSTED') {
    actionButtons.push(
      <InvButton key="reverse" size="small" tone="red" variant="outlined"
        onClick={() => setDialog({
          action: 'reverse', title: 'Reverse Invoice',
          desc: 'Reverse this posted invoice? This cannot be undone.',
          destructive: true, requireReason: true, reasonLabel: 'Reversal reason',
        })}>
        Reverse Invoice
      </InvButton>,
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Purchase Invoices"
        title={invoice.invoice_number || 'Purchase Invoice'}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(`${base}/purchase-invoices`)}>
              Back to List
            </InvButton>
            {actionButtons}
          </>
        }
      />

      {status !== 'REVERSED' && (
        <SectionCard sx={{ mb: 2.5 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {LIFECYCLE_STEPS.map((step) => (
              <Step key={step}>
                <StepLabel>{step === 'MATCHED' && status === 'VARIANCE' ? 'MATCHED / VARIANCE' : step}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </SectionCard>
      )}

      {status === 'DRAFT' && (
        <Box
          role="note"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2.5, px: 2, py: 1.5,
            borderRadius: 1, border: `1px solid ${IPD_COLORS.border}`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: IPD_COLORS.primary, mt: 0.2, flexShrink: 0 }} aria-hidden="true" />
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, lineHeight: 1.6 }}>
            This invoice is in <strong>DRAFT</strong>. Click{' '}
            <strong>Run 3-Way Match</strong> (top right) to verify quantities and rates against
            the purchase order and GRN before posting to finance.
          </Typography>
        </Box>
      )}

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <SectionCard title="Invoice Details" sx={{ height: '100%' }} actions={<StatusChip status={status} />}>
            <DetailRow label="Vendor">{resolveVendorName(invoice.vendor_id)}</DetailRow>
            <DetailRow label="Purchase Order">{poNumber || invoice.purchase_order_id}</DetailRow>
            <DetailRow label="Source GRN">{grnNumber || invoice.grn_id}</DetailRow>
            <DetailRow label="Invoice Date">{formatDate(invoice.invoice_date)}</DetailRow>
            <DetailRow label="Due Date">{formatDate(invoice.due_date)}</DetailRow>
            <DetailRow label="Payment Terms">{invoice.payment_terms || '—'}</DetailRow>
            <DetailRow label="Match Status">
              {invoice.match_status ? <StatusChip status={invoice.match_status} /> : '—'}
            </DetailRow>
            {status !== 'DRAFT' && (
              <>
                <DetailRow label="Matched By">{invoice.matched_by || '—'}</DetailRow>
                <DetailRow label="Matched On">
                  {invoice.matched_at ? formatDateTime(invoice.matched_at) : '—'}
                </DetailRow>
              </>
            )}
            <DetailRow label="Posted to Finance">{invoice.posted_to_finance ? 'Yes' : 'No'}</DetailRow>
            <DetailRow label="Finance Ref">
              {invoice.finance_ref ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography component="span" sx={{ fontSize: 'inherit', fontFamily: 'monospace' }}>
                    {invoice.finance_ref}
                  </Typography>
                  <Tooltip title={financeRefCopied ? 'Copied!' : 'Copy reference'}>
                    <IconButton
                      size="small"
                      onClick={handleCopyFinanceRef}
                      aria-label="Copy finance reference"
                      sx={{ p: 0.25, ml: 0.25 }}
                    >
                      <ContentCopyIcon
                        sx={{ fontSize: 14, color: financeRefCopied ? IPD_COLORS.primary : IPD_COLORS.textSecondary }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : '—'}
            </DetailRow>
            <DetailRow label="Payment Status">
              <PaymentStatusBadge status={invoice.payment_status} />
            </DetailRow>
            {invoice.expected_payment_date && (
              <DetailRow label="Expected Payment">{formatDate(invoice.expected_payment_date)}</DetailRow>
            )}
            <DetailRow label="Created">{formatDateTime(invoice.created_at)}</DetailRow>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title="Invoice Line Items" sx={{ height: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720, '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rate</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Line Total</TableCell>
                    {lines.some(l => l.match_status) && (
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Match</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 3 }}>
                        No lines
                      </TableCell>
                    </TableRow>
                  ) : lines.map((line, i) => (
                    <TableRow key={line.purchase_invoice_line_id || line.id || i}>
                      <TableCell>{resolveItem(line)}</TableCell>
                      <TableCell align="right">{formatQty(line.invoiced_quantity)}</TableCell>
                      <TableCell align="right">{formatCurrency(line.unit_rate)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(invoiceLineTotal(line))}</TableCell>
                      {lines.some(l => l.match_status) && (
                        <TableCell>{line.match_status ? <StatusChip status={line.match_status} /> : '—'}</TableCell>
                      )}
                    </TableRow>
                  ))}
                  {lines.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right"
                        sx={{ fontWeight: 700, color: IPD_COLORS.primary, borderTop: `2px solid ${IPD_COLORS.border}` }}>
                        Grand Total
                      </TableCell>
                      <TableCell align="right"
                        sx={{ fontWeight: 700, color: IPD_COLORS.primary, borderTop: `2px solid ${IPD_COLORS.border}`, fontFamily: 'monospace' }}>
                        {formatCurrency(grandTotal)}
                      </TableCell>
                      {lines.some(l => l.match_status) && <TableCell sx={{ borderTop: `2px solid ${IPD_COLORS.border}` }} />}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {status === 'VARIANCE' && (
        <Alert severity="warning" sx={{ mt: 2.5 }}>
          {varianceItems?.length > 0 ? (
            <>
              Variance found on {varianceItems.length} line{varianceItems.length !== 1 ? 's' : ''}
              {': '}{varianceItems.map(v => v.label).join(', ')}.
              {' '}Review the match comparison table below, then post with an override justification if acceptable.
            </>
          ) : (
            'A quantity or rate variance was found — review the match comparison table below, then post with an override justification if the variance is acceptable.'
          )}
        </Alert>
      )}
      {status === 'POSTED' && (
        <Alert severity="success" sx={{ mt: 2.5 }}>
          This invoice has been queued for accounts/finance payment.
          {invoice.finance_ref ? ` Finance reference: ${invoice.finance_ref}.` : ''}
        </Alert>
      )}

      {MATCH_RUN_STATUSES.has(status) && (
        <VarianceBreakdownSection
          lines={lines}
          poItems={poItems}
          grnItems={grnItems}
          loading={comparisonLoading}
          resolveItem={resolveItem}
        />
      )}

      <Dialog
        open={matchOpen}
        onClose={matching ? undefined : () => setMatchOpen(false)}
        PaperProps={{ sx: { ...cardSx, minWidth: 380, p: 1 } }}
      >
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Run 3-Way Match</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: IPD_COLORS.textSecondary, mb: 2 }}>
            Compares invoiced quantity and rate against the purchase order and the GRN received quantity.
          </DialogContentText>
          <InvTextField
            label="Tolerance %"
            type="number"
            value={tolerance}
            inputProps={{ min: 0, max: 100, step: '0.1' }}
            onChange={e => setTolerance(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setMatchOpen(false)} disabled={matching} variant="outlined"
            sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
            Cancel
          </Button>
          <InvButton loading={matching} onClick={handleRunMatch}>Run Match</InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason} reasonLabel={dialog?.reasonLabel}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
